import { useCallback, useEffect } from 'react';
import { useDataProvider } from 'react-admin';
import { TextField, Select, MenuItem, FormControl, InputLabel, Grid, Checkbox, FormControlLabel, Alert, Typography } from '@mui/material';
import { z } from 'zod';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { buildTransitionChain, getMachineNextState, useRuleEvaluation } from '@/components/workflow';

const STEPS = [
  { id: 'details', title: 'Event Details' },
  { id: 'attendees', title: 'Expected Attendees' },
  { id: 'recurrence', title: 'Recurrence' },
  { id: 'reminders', title: 'Reminders' },
  { id: 'conduct', title: 'Conduct Event' },
];

const EVENT_TYPES = [
  'Evacuation Drill',
  'Monthly H&S Meeting',
  'Fire Extinguisher Check',
  'First Aid Training',
  'OHS Training',
];

const RECURRENCE_OPTIONS = ['None', 'Quarterly', 'Monthly', 'Annually'];
const ATTENDEE_OPTIONS = ['Select Employees', 'All Staff'];
const COMPLETION_STATUS = ['Yes', 'No', 'Cancelled'];

interface FormValues {
  eventType: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  attendeeType: string;
  selectedEmployees: string;
  recurrence: string;
  reminder7Days: boolean;
  reminder1Day: boolean;
  actualAttendees: string;
  completed: string;
  minutes: string;
  followUpActions: string;
}

const INITIAL_VALUES: FormValues = {
  eventType: '',
  scheduledDate: '',
  scheduledTime: '',
  location: '',
  attendeeType: 'All Staff',
  selectedEmployees: '',
  recurrence: 'None',
  reminder7Days: true,
  reminder1Day: true,
  actualAttendees: '',
  completed: '',
  minutes: '',
  followUpActions: '',
};

const stepSchemas = {
  details: z.object({
    eventType: z.string().min(1, 'Event type is required'),
    scheduledDate: z.string().min(1, 'Scheduled date is required'),
    scheduledTime: z.string().min(1, 'Scheduled time is required'),
    location: z.string().trim().min(1, 'Location is required'),
  }).refine(
    (data) => {
      const eventDate = new Date(data.scheduledDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return eventDate >= today;
    },
    { message: 'Scheduled date cannot be in the past', path: ['scheduledDate'] },
  ),

  attendees: z.object({
    attendeeType: z.string(),
    selectedEmployees: z.string(),
  }).refine(
    (data) => {
      if (data.attendeeType === 'Select Employees') {
        return data.selectedEmployees.trim().length > 0;
      }
      return true;
    },
    { message: 'Please select at least one employee', path: ['selectedEmployees'] },
  ),

  conduct: z.object({
    completed: z.string().min(1, 'Completion status is required'),
    actualAttendees: z.string(),
  }).refine(
    (data) => {
      if (data.completed === 'Yes') {
        return data.actualAttendees.trim().length > 0;
      }
      return true;
    },
    { message: 'Actual attendees count is required', path: ['actualAttendees'] },
  ),
};

const validateStep = (stepId: string, values: FormValues) => {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };
  return stepValidator(schema)(values as unknown as Record<string, unknown>);
};

export const CalendarEventWizard = () => {
  const dataProvider = useDataProvider();
  
  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      eventId: null,
      eventType: v.eventType,
      scheduledDate: v.scheduledDate,
      location: v.location,
      attendees: v.attendeeType === 'All Staff' ? ['all_staff'] : (v.selectedEmployees ? v.selectedEmployees.split('\n').map((s: string) => s.trim()).filter(Boolean) : []),
      description: null,
      recurrenceRule: v.recurrence !== 'None' ? v.recurrence : null,
      actualAttendees: v.actualAttendees ? [v.actualAttendees] : [],
      minutesRef: v.minutes || null,
      findings: v.followUpActions ? [v.followUpActions] : [],
    };

    const eventResult = await dataProvider.create('ohsCalendarEvents', { data: formValues });
    const eventId = eventResult.data.id;

    const wizardEvents = ['PUBLISH'];
    const { finalState: publishedState, transitions: publishedTransitions } = buildTransitionChain('ohs-calendar-event', 'draft', wizardEvents);

    let currentState = publishedState;
    const transitions = [...publishedTransitions];

    if (v.completed === 'Yes') {
      const startNext = getMachineNextState('ohs-calendar-event', currentState, 'START');
      if (startNext) {
        transitions.push({
          sequence: transitions.length + 1,
          event: 'START',
          fromState: currentState,
          toState: startNext,
          timestamp: new Date().toISOString(),
          triggeredBy: 'current-user',
        });
        currentState = startNext;
      }
      const completeNext = getMachineNextState('ohs-calendar-event', currentState, 'COMPLETE');
      if (completeNext) {
        transitions.push({
          sequence: transitions.length + 1,
          event: 'COMPLETE',
          fromState: currentState,
          toState: completeNext,
          timestamp: new Date().toISOString(),
          triggeredBy: 'current-user',
        });
        currentState = completeNext;
      }
    }

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'ohs-calendar-event',
        entityId: eventId,
        entityName: 'Event: ' + v.eventType,
        currentState: { value: currentState, context },
        status: currentState === 'cancelled' ? 'cancelled' : 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: eventId,
        transitions,
      },
    });

    return { id: eventId };
  }, [dataProvider]);

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: validateStepHook,
    showFeedback,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'ohs-calendar-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      eventType: (values as unknown as FormValues).eventType,
      scheduledDate: (values as unknown as FormValues).scheduledDate,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'conduct') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const vals = values as unknown as FormValues;

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value } as Record<string, unknown>);
  };

  const handleNext = () => {
    const result = validateStep(currentStep.id, vals);
    if (!result.isValid) {
      return;
    }

    if (currentStep.id === 'details') {
      showFeedback('success', ['Event scheduled for ' + vals.scheduledDate + ' at ' + vals.scheduledTime]);
    }
    if (currentStep.id === 'recurrence') {
      showFeedback('success', ['Recurrence set to ' + vals.recurrence]);
    }
    if (currentStep.id === 'reminders') {
      showFeedback('success', ['Reminders configured: 7-day and 1-day alerts active']);
    }

    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleReset = () => {
    reset();
  };

  const handleBack = () => {
    prevStep();
  };

  const handleComplete = async () => {
    complete();
  };

  const handleSaveDraft = async () => {
    if (dataProvider) {
      await dataProvider.create('ohsCalendarEvents', { data: { ...vals, _draft: true } });
      showFeedback('success', ['Draft saved successfully']);
    }
  };

  if (isCompleted) {
    return (
      <WizardLayout
        steps={STEPS}
        currentStepIndex={currentStepIndex}
        progress={progress}
        isSaving={isSaving}
        isCompleted={isCompleted}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        onReset={handleReset}
      >
        <OutcomeSummary
          title="OHS Calendar Event Recorded"
          processId={createdId || undefined}
          fields={[
            { label: 'Event Type', value: vals.eventType },
            { label: 'Scheduled Date', value: vals.scheduledDate },
            { label: 'Scheduled Time', value: vals.scheduledTime },
            { label: 'Location', value: vals.location },
            { label: 'Recurrence', value: vals.recurrence },
            { label: 'Completion Status', value: vals.completed },
            { label: 'Actual Attendees', value: vals.actualAttendees || 'N/A' },
          ]}
          footerActions={[
            { label: 'View Events', to: '/ohsCalendarEvents' },
            { label: 'Start Another', onClick: handleReset },
          ]}
        />
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={handleBack}
      onSaveDraft={handleSaveDraft}
      onNext={handleNext}
      onComplete={handleComplete}
      onReset={handleReset}
    >
      <WizardStep
        stepNumber={currentStepIndex + 1}
        totalSteps={totalSteps}
        title={currentStep.title}
        description={getStepDescription(currentStep.id)}
        feedback={stepFeedback}
      >
        {renderFormFields(currentStep.id, vals, errors, updateField, ruleResult)}
      </WizardStep>
    </WizardLayout>
  );
};

function getStepDescription(stepId: string): string {
  const descriptions: Record<string, string> = {
    details: 'Capture event details including type, date, time, and location per OHS Act requirements',
    attendees: 'Specify expected attendees for the OHS event',
    recurrence: 'Set recurrence pattern for recurring OHS events',
    reminders: 'Configure automated reminders for the event',
    conduct: 'Record attendance, completion status, and follow-up actions',
  };
  return descriptions[stepId] || '';
}

function renderFormFields(stepId: string, vals: FormValues, errors: Record<string, string>, updateField: (field: keyof FormValues, value: unknown) => void, ruleResult: { allowed: boolean; reason?: string } | null): React.ReactNode {
    switch (stepId) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.eventType}>
                <InputLabel>Event Type</InputLabel>
                <Select value={vals.eventType} label="Event Type"
                  onChange={e => updateField('eventType', e.target.value)}>
                  {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              {errors.eventType && <Typography variant="caption" color="error">{errors.eventType}</Typography>}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Scheduled Date" type="date" value={vals.scheduledDate}
                onChange={e => updateField('scheduledDate', e.target.value)} fullWidth
                InputLabelProps={{ shrink: true }} error={!!errors.scheduledDate}
                helperText={errors.scheduledDate || 'Date of the OHS event'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Scheduled Time" type="time" value={vals.scheduledTime}
                onChange={e => updateField('scheduledTime', e.target.value)} fullWidth
                InputLabelProps={{ shrink: true }} error={!!errors.scheduledTime}
                helperText={errors.scheduledTime} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Location" value={vals.location}
                onChange={e => updateField('location', e.target.value)} fullWidth
                error={!!errors.location} helperText={errors.location || 'Venue or area for the event'} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                OHS Act: Employers must ensure safe evacuation drills and regular H&S meetings.
                Fire extinguisher checks are mandatory under the OHS Fire Regulations.
              </Alert>
            </Grid>
          </Grid>
        );

      case 'attendees':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Attendee Type</InputLabel>
                <Select value={vals.attendeeType} label="Attendee Type"
                  onChange={e => updateField('attendeeType', e.target.value)}>
                  {ATTENDEE_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {vals.attendeeType === 'Select Employees' && (
              <Grid size={{ xs: 12 }}>
                <TextField label="Select Employees" value={vals.selectedEmployees}
                  onChange={e => updateField('selectedEmployees', e.target.value)} fullWidth
                  multiline rows={3} error={!!errors.selectedEmployees}
                  helperText={errors.selectedEmployees || 'Enter employee names or IDs, one per line'} />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                OHS Act: First aid training requires a qualified first aider present.
                Evacuation drills must include all building occupants.
              </Alert>
            </Grid>
          </Grid>
        );

      case 'recurrence':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Recurrence</InputLabel>
                <Select value={vals.recurrence} label="Recurrence"
                  onChange={e => updateField('recurrence', e.target.value)}>
                  {RECURRENCE_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Recurrence Guidelines</Typography>
                <Typography variant="body2">• Monthly: Monthly H&S committee meetings</Typography>
                <Typography variant="body2">• Quarterly: Fire extinguisher inspections, first aid training renewals</Typography>
                <Typography variant="body2">• Annually: Evacuation drill reviews, OHS training updates</Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      case 'reminders':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={vals.reminder7Days}
                    onChange={e => updateField('reminder7Days', e.target.checked)}
                  />
                }
                label="Send reminder 7 days before event"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={vals.reminder1Day}
                    onChange={e => updateField('reminder1Day', e.target.checked)}
                  />
                }
                label="Send reminder 1 day before event"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="success">
                Reminders will be sent to all {vals.attendeeType === 'All Staff' ? 'staff members' : 'selected employees'} at the configured intervals.
              </Alert>
            </Grid>
          </Grid>
        );

      case 'conduct':
        return (
          <Grid container spacing={2}>
            {ruleResult && !ruleResult.allowed && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Guard Check</Typography>
                  <Typography variant="body2">{ruleResult.reason}</Typography>
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.completed}>
                <InputLabel>Completed</InputLabel>
                <Select value={vals.completed} label="Completed"
                  onChange={e => updateField('completed', e.target.value)}>
                  {COMPLETION_STATUS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              {errors.completed && <Typography variant="caption" color="error">{errors.completed}</Typography>}
            </Grid>
            {vals.completed === 'Yes' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Actual Attendees" type="number" value={vals.actualAttendees}
                  onChange={e => updateField('actualAttendees', e.target.value)} fullWidth
                  error={!!errors.actualAttendees} helperText={errors.actualAttendees} />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField label="Meeting Minutes / Event Notes" value={vals.minutes}
                onChange={e => updateField('minutes', e.target.value)} fullWidth
                multiline rows={4} placeholder="Record key discussions, decisions, and outcomes..." />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Follow-up Actions" value={vals.followUpActions}
                onChange={e => updateField('followUpActions', e.target.value)} fullWidth
                multiline rows={3} placeholder="List any follow-up actions required..." />
            </Grid>
            {vals.completed === 'Yes' && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="success">
                  Event completed. Follow-up actions have been recorded for compliance tracking.
                </Alert>
              </Grid>
            )}
            {vals.completed === 'Cancelled' && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  Event cancelled. OHS Act requires that mandatory events be rescheduled.
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      default:
        return null;
    }
  }